/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import Box from "@material-ui/core/Box";
import FavoriteIcon from "@material-ui/icons/Favorite";
import BorderLoader from "components/atoms/loader/BorderLoader";
import {
  MEDIA_QUERY_CONDITION_FOR_PC,
  MEDIA_QUERY_FOR_PC,
} from "constants/mediaQueryForPc";
import { MEDIA_QUERY_FOR_SP } from "constants/mediaQueryForSp";
import { useUserWatchlist } from "features/watchlist/useWatchlist";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useHistory } from "react-router-dom";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";

import { AccountWatchlistCard } from "./AccountWatchlistCard";
import { AccountWatchlistModal } from "./AccountWatchlistModal";

import type { WatchlistItem } from "features/watchlist/types/WatchlistItem";

type Props = Record<string, never>;

export const AccountWatchList: React.FC<Props> = () => {
  const { t } = useI18n();
  const { userWatchlist, removeFromWatchlist, addToCart } = useUserWatchlist();
  const dispatch = useDispatch();
  const [selectedItemForRemoval, setSelectedItemForRemoval] =
    useState<WatchlistItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const classes = watchListStyles();
  const history = useHistory();
  const isPC = useMediaQuery(MEDIA_QUERY_CONDITION_FOR_PC);

  const showRemoveConfirmModal = (item: WatchlistItem) => {
    setSelectedItemForRemoval(item);
    setIsDeleting(false);
  };

  const closeConfirmModal = () => {
    setSelectedItemForRemoval(null);
  };

  const confirmRemoveFromWatchList = async () => {
    if (isDeleting || selectedItemForRemoval === null) {
      return;
    }
    setIsDeleting(true);

    try {
      await removeFromWatchlist(selectedItemForRemoval.eventGoodsFirestoreId);
      closeConfirmModal();
    } catch (err) {
      console.error(err);
      dispatch(
        modalActions.toggleError({
          msg: "削除に失敗しました",
        })
      );
      closeConfirmModal();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      width={"100%"}
      maxWidth={"1800px"}
      minHeight={["calc(100vh - 436px)", "calc(100vh - 280px)"]}
      paddingBottom={"80px"}
      margin={"0 auto"}
      bgcolor={"#fafafa"}
    >
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          { label: t("common.routes.account"), link: "/account" },
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          { label: "ウォッチリスト", link: null },
        ]}
      />
      <Box css={classes.container}>
        <Box css={classes.header}>
          <h2>
            {isPC ? (
              <FavoriteIcon fontSize="large" />
            ) : (
              <FavoriteIcon fontSize="small" />
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
            )}
            ウォッチリスト
          </h2>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <p>
            ウォッチリストに登録すると気になる商品へあとから簡単にアクセスできます。
          </p>
        </Box>
        {!userWatchlist ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <BorderLoader />
          </Box>
        ) : userWatchlist.length > 0 ? (
          <div css={classes.goodsList}>
            {userWatchlist.map((item: WatchlistItem) => (
              <AccountWatchlistCard
                key={item.eventGoodsFirestoreId}
                item={item}
                onClickRemove={() => showRemoveConfirmModal(item)}
                onClickCard={() => {
                  history.push(
                    `/events/${
                      item.eventFirestoreId
                    }/goods/${encodeURIComponent(item.className)}`
                  );
                }}
                onClickAddToCart={() => addToCart(item)}
              />
            ))}
          </div>
        ) : (
          <div css={classes.notApplicable}>
            {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
            <p>登録されている商品がありません</p>
          </div>
        )}
      </Box>
      <AccountWatchlistModal
        selectedItem={selectedItemForRemoval ?? undefined}
        onClickClose={closeConfirmModal}
        onClickConfirmRemove={confirmRemoveFromWatchList}
        isLoadingConfirmRemoveButton={isDeleting}
      />
    </Box>
  );
};

const watchListStyles = () => {
  return {
    container: css`
      width: 95%;
      margin: 0 auto;
      padding: 2px;
    `,
    header: css`
      margin-bottom: 30px;
      text-align: left;
      h2 {
        font-weight: bold;
        color: #333;
        display: flex;
        align-items: center;
        gap: 8px;
        ${MEDIA_QUERY_FOR_SP} {
          height: 36px;
          font-size: 18px;
          margin-top: 18px;
          margin-bottom: 18px;
        }
        ${MEDIA_QUERY_FOR_PC} {
          height: 56px;
          font-size: 24px;
          margin-top: 24px;
          margin-bottom: 24px;
        }
      }
      p {
        ${MEDIA_QUERY_FOR_SP} {
          font-size: 14px;
          line-height: 20px;
        }
        ${MEDIA_QUERY_FOR_PC} {
          font-size: 16px;
          line-height: 24px;
        }
      }
    `,
    goodsList: css`
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: initial;
      gap: 20px;
      ${MEDIA_QUERY_FOR_SP} {
        grid-template-columns: repeat(1, 1fr);
        gap: 16px;
      }
    `,
    notApplicable: css`
      text-align: center;
      padding: 60px 20px;
      p {
        font-size: 16px;
        color: #666;
      }
    `,
  };
};
